import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { User, MessageCircle, Plus, LogOut, LayoutGrid, ShieldAlert } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { StateSelect } from "@/components/StateSelect";
import { useAuth, useRegion } from "@/context/AuthContext";
import { api } from "@/lib/api";

function useUnread(user, path) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!user) return;
    const load = () => { if (!document.hidden) api.get("/conversations/unread-count").then((r) => setN(r.data.count)).catch(() => {}); };
    load();
    const t = setInterval(load, 20000);
    window.addEventListener("isoldit:unread", load);
    document.addEventListener("visibilitychange", load);
    return () => { clearInterval(t); window.removeEventListener("isoldit:unread", load); document.removeEventListener("visibilitychange", load); };
  }, [user, path]);
  return n;
}

export default function Header() {
  const { user, logout } = useAuth();
  const { region, setRegion } = useRegion();
  const nav = useNavigate();
  const loc = useLocation();
  const unread = useUnread(user, loc.pathname);

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0f2a33]/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <Logo />
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <div className="hidden md:block"><StateSelect value={region} onChange={setRegion} testId="header-state-select" /></div>
          <Link to="/sell" data-testid="header-sell-btn" className="btn-lime hidden sm:inline-flex h-10 px-4 text-xs">
            <Plus className="h-4 w-4" /> Sell free
          </Link>
          {user && (
            <Link to="/messages" data-testid="header-messages-link" className="relative grid h-10 w-10 place-items-center rounded-full text-white hover:bg-white/10 transition-colors">
              <MessageCircle className="h-5 w-5" />
              {unread > 0 && <span data-testid="header-unread-badge" className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[#d4f253] px-1 text-[10px] font-bold text-[#0f2a33]">{unread}</span>}
            </Link>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="header-user-menu" className="grid h-10 w-10 place-items-center rounded-full bg-white/10 font-bold text-[#d4f253] hover:bg-white/15 transition-colors">
                {user.name?.[0]?.toUpperCase()}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm font-semibold text-[#0f2a33]">{user.name}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav("/dashboard")}><LayoutGrid className="mr-2 h-4 w-4" />My listings</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-messages" onClick={() => nav("/messages")}><MessageCircle className="mr-2 h-4 w-4" />Messages</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-sell" onClick={() => nav("/sell")}><Plus className="mr-2 h-4 w-4" />Sell an item</DropdownMenuItem>
                {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}><ShieldAlert className="mr-2 h-4 w-4" />Reports</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="header-login-link" className="grid h-10 w-10 place-items-center rounded-full text-white hover:bg-white/10 transition-colors" aria-label="Log in">
              <User className="h-5 w-5" />
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
